import {
  logout as apiLogout,
  cancelTokenRefresh,
  loginWithPassword,
  refreshToken,
  scheduleTokenRefresh,
} from '@xstudios/xapp-auth/services/fastApiTokenAuthService'
import { defineStore } from 'pinia'

export const useFastApiTokenAuthStore = defineStore('fastapi-token-auth', {
  actions: {
    _matches(userPerm, requiredPattern) {
      const [uNs, uAct] = this._splitPerm(userPerm)
      const [rNs, rAct] = this._splitPerm(requiredPattern)
      const nsOk = uNs === '*' || rNs === '*' || uNs === rNs
      const actOk = uAct === '*' || rAct === '*' || uAct === rAct
      return nsOk && actOk
    },

    _splitPerm(s) {
      const [ns = '*', act = '*'] = String(s).toLowerCase().split(':')
      return [ns || '*', act || '*']
    },

    async bootstrap() {
      if (this.bootstrapped) return
      await this.silentRefresh()
      this.bootstrapped = true
    },

    can(required) {
      if (!required) return false
      if (this.user?.is_superuser) return true
      if (this.permissions.has('*') || this.permissions.has('*:*')) return true
      for (const grant of this.permissions) {
        if (this._matches(grant, required)) return true
      }
      return false
    },

    canAll(requiredList) {
      if (!Array.isArray(requiredList) || requiredList.length === 0) return true
      return requiredList.every((p) => this.can(p))
    },

    canAny(requiredList) {
      if (!Array.isArray(requiredList) || requiredList.length === 0)
        return false
      return requiredList.some((p) => this.can(p))
    },

    clearSession() {
      this.setToken(null)
      this.user = null
      this.permissions = new Set()
    },

    async login(email, password) {
      const session = await loginWithPassword(email, password)
      this.setSession(session)
    },

    async logout() {
      await apiLogout()
      this.clearSession()
    },

    setSession({ access_token, permissions, user }) {
      this.setToken(access_token)
      this.user = user
      this.permissions = new Set(
        (permissions || []).map((p) => String(p).trim().toLowerCase())
      )
    },

    setToken(token) {
      this.token = token
      this.authenticated = !!token
      if (token) {
        scheduleTokenRefresh(token)
      } else {
        cancelTokenRefresh()
      }
    },

    async silentRefresh() {
      let session = null
      try {
        session = await refreshToken()
      } catch {
        session = null
      }
      if (session) {
        this.setSession(session)
      } else {
        this.clearSession()
      }
    },
  },
  state: () => ({
    authenticated: false,
    bootstrapped: false,
    permissions: new Set(),
    token: null,
    user: null,
  }),
})
